import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Card,
  Stack,
  Chip,
  IconButton,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { useTheme, styled, alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  CheckCircle,
  Cancel,
  SwapHoriz,
  VideoLibrary,
  Close,
  Person,
  CalendarToday,
  AttachMoney,
  TrendingUp,
  ExpandMore,
  ExpandLess,
  ChatBubbleOutline,
} from "@mui/icons-material";
import client from "../../../API";
import useAlertDialog from "../../../Components/useAlertDialog";
import AlertDialog from "../../../Components/AlertDialog";
import ChatScreen from "../../../Components/ChatScreen";
import AgencyNegotiateDialog from "./agencyNegotiate";
import { formatMessageContent } from "../../../Utils/constants";

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    maxHeight: "90vh",
    borderRadius: 16,
    [theme.breakpoints.down("md")]: {
      margin: theme.spacing(1),
      width: "calc(100% - 16px)",
      maxHeight: "calc(100vh - 16px)",
    },
  },
}));

const CompactSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

// Utility Functions
function formatIdealDueDate(dateString) {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date";
  return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${String(date.getFullYear()).slice(-2)}`;
}

const AgencyCampaignPitchDialog = ({
  openDialog,
  handleCloseDialog,
  campaign,
  username,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [openNegotiateDialog, setOpenNegotiateDialog] = useState(false);
  const [showBrief, setShowBrief] = useState(false);
  const [showChat, setShowChat] = useState(false);
  
  const queryClient = useQueryClient();
  const { dialogState, openDialog: openAlert, closeDialog } = useAlertDialog();

  // Queries
  const {
    data: conversation,
    isLoading: isConversationLoading,
  } = useQuery(
    ["conversation", campaign?.id, username],
    () =>
      client.conversations.checkCampaign({
        campaign_id: campaign?.id,
        creator_id: username,
      }),
    {
      enabled: !!campaign && !!username && openDialog && showChat,
      refetchOnWindowFocus: false,
    }
  );

  // Derived Data
  const creator = useMemo(() => {
    if (!campaign) return undefined;
    return campaign.creators.find((creator) => creator.id === username);
  }, [campaign, username]);

  const currentRate = useMemo(
    () => creator?.agencyRate || creator?.price || 0,
    [creator]
  );

  const videoList = useMemo(
    () => creator?.videos.filter((video) => video.parentId == null) || [],
    [creator]
  );

  const crosspostList = useMemo(
    () => creator?.videos.filter((video) => video.parentId != null) || [],
    [creator]
  );

  const deliverableCount = useMemo(
    () => videoList.length + crosspostList.length,
    [videoList, crosspostList]
  );

  // Mutations
  const { mutate: updateStatus, isLoading: isUpdatingStatus } = useMutation(
    (input) => client.agency.campaignAccept(campaign.id, input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["creators", username]);
        queryClient.invalidateQueries(["campaigns"]);
      },
      onError: (error) => {
        openAlert(
          "Error",
          `Error updating status: ${error.response?.data?.error || error.message}`,
          closeDialog
        );
      },
    }
  );

  // Handlers
  const handleChangeStatus = (accepted) => {
    updateStatus(
      { status: accepted ? "Accepted" : "Declined", creator: username },
      {
        onSuccess: () => {
          const title = accepted ? "Campaign Accepted" : "Campaign Rejected";
          const message = accepted
            ? "You have successfully accepted this campaign pitch."
            : "You have rejected this campaign pitch.";
          openAlert(title, message, () => {
            closeDialog();
            handleCloseDialog();
          });
        },
      }
    );
  };

  const onClickOption = (accepted = false) => {
    const title = accepted ? "Accept Campaign" : "Reject Campaign";
    const message = accepted
      ? "The creator will be notified of upcoming due dates and receive messages from the campaign manager. Continue?"
      : "Rejecting this campaign is irreversible and will remove it from your dashboard. Continue?";
    openAlert(
      title,
      message,
      () => {
        closeDialog();
        handleChangeStatus(accepted);
      },
      closeDialog
    );
  };

  if (!campaign) return null;

  return (
    <>
      <StyledDialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        {/* Compact Header */}
        <Box
          sx={{
            p: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Box sx={{ flex: 1, pr: 2 }}>
              <Typography variant="h5" fontWeight="700" gutterBottom>
                {campaign.name}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                <Chip
                  icon={<Person />}
                  label={username}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<CalendarToday />}
                  label={formatIdealDueDate(campaign.ideal_duedate)}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </Box>
            <IconButton onClick={handleCloseDialog} size="small">
              <Close />
            </IconButton>
          </Stack>

          {/* Key Metrics Row */}
          <Stack
            direction="row"
            spacing={2}
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: "background.paper",
              borderRadius: 2,
            }}
          >
            <Box sx={{ flex: 1, textAlign: "center" }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <AttachMoney color="success" />
                <Typography variant="h6" fontWeight="700" color="success.main">
                  ${currentRate.toFixed(2)}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Proposed Rate
              </Typography>
            </Box>
            
            <Divider orientation="vertical" flexItem />
            
            <Box sx={{ flex: 1, textAlign: "center" }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <VideoLibrary color="primary" />
                <Typography variant="h6" fontWeight="700">
                  {deliverableCount}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Deliverables
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Content */}
        <DialogContent sx={{ p: 2.5 }}>
          {isUpdatingStatus ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                py: 8,
                gap: 2,
              }}
            >
              <CircularProgress size={48} />
              <Typography variant="body1" color="text.secondary">
                Updating campaign status...
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {/* Status Alert */}
              {creator?.status && (
                <Alert
                  severity={
                    creator.status === "Counter" ? "warning" : "info"
                  }
                  sx={{ borderRadius: 2 }}
                >
                  <Typography variant="body2" fontWeight="600">
                    Status: {creator.status}
                  </Typography>
                  <Typography variant="caption">
                    {creator.status === "Pitched" && "Awaiting your review"}
                    {creator.status === "Counter" && "Creator has made a counter-offer"}
                  </Typography>
                </Alert>
              )}

              {/* Deliverables - Always Visible */}
              <CompactSection>
                <Typography variant="subtitle2" fontWeight="700" gutterBottom sx={{ mb: 1.5 }}>
                  📦 Deliverables Overview
                </Typography>
                
                {/* Primary Videos */}
                <Box sx={{ mb: videoList.length > 0 && crosspostList.length > 0 ? 1.5 : 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    Primary Content ({videoList.length})
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                    {videoList.map((video) => (
                      <Chip
                        key={video.id}
                        icon={<CheckCircle />}
                        label={`${video.promotionPlatform} ${video.promotionType}`}
                        size="small"
                        color="primary"
                        variant="filled"
                      />
                    ))}
                  </Stack>
                </Box>

                {/* Crossposts */}
                {crosspostList.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                      Crossposts ({crosspostList.length})
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                      {crosspostList.map((video) => (
                        <Chip
                          key={video.id}
                          icon={<TrendingUp />}
                          label={video.promotionPlatform}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </CompactSection>

              {/* Creator Instructions - If Present */}
              {creator?.postingInstructions && (
                <CompactSection>
                  <Typography variant="subtitle2" fontWeight="700" gutterBottom>
                    📝 Creator Notes
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                    {creator.postingInstructions}
                  </Typography>
                </CompactSection>
              )}

              {/* Campaign Brief - Collapsible */}
              <Paper
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: "hidden",
                }}
              >
                <Box
                  onClick={() => setShowBrief(!showBrief)}
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="700">
                    📄 Campaign Brief
                  </Typography>
                  {showBrief ? <ExpandLess /> : <ExpandMore />}
                </Box>
                {showBrief && (
                  <Box
                    sx={{
                      p: 2,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      maxHeight: 300,
                      overflow: "auto",
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                      {formatMessageContent(campaign.brief)}
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* Chat - Collapsible */}
              <Paper
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: "hidden",
                }}
              >
                <Box
                  onClick={() => setShowChat(!showChat)}
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: alpha(theme.palette.info.main, 0.02),
                    "&:hover": { bgcolor: alpha(theme.palette.info.main, 0.05) },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ChatBubbleOutline fontSize="small" />
                    <Typography variant="subtitle2" fontWeight="700">
                      Brand Chat
                    </Typography>
                  </Stack>
                  {showChat ? <ExpandLess /> : <ExpandMore />}
                </Box>
                {showChat && (
                  <Box
                    sx={{
                      borderTop: `1px solid ${theme.palette.divider}`,
                      height: 400,
                    }}
                  >
                    {isConversationLoading ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        <CircularProgress size={32} />
                      </Box>
                    ) : conversation?.conversation_id ? (
                      <ChatScreen
                        conversationId={conversation.conversation_id}
                        specificReaderId={username}
                        sx={{ height: "100%" }}
                        showWarning={false}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          color: "text.secondary",
                        }}
                      >
                        <ChatBubbleOutline sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                        <Typography variant="body2">
                          No conversation started yet
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            </Stack>
          )}
        </DialogContent>

        {/* Prominent Actions */}
        <DialogActions
          sx={{
            p: 2.5,
            borderTop: `2px solid ${theme.palette.divider}`,
            gap: 1.5,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Button
            onClick={handleCloseDialog}
            variant="text"
            color="inherit"
            fullWidth={isMobile}
          >
            Close
          </Button>
          
          <Box sx={{ flex: 1 }} />
          
          <Button
            variant="outlined"
            onClick={() => onClickOption(false)}
            color="error"
            disabled={isUpdatingStatus || creator?.status === "Counter"}
            startIcon={<Cancel />}
            fullWidth={isMobile}
            sx={{ minWidth: 120 }}
          >
            Reject
          </Button>
          
          <Button
            variant="contained"
            onClick={() => setOpenNegotiateDialog(true)}
            color="warning"
            disabled={isUpdatingStatus || creator?.status === "Counter"}
            startIcon={<SwapHoriz />}
            fullWidth={isMobile}
            sx={{ minWidth: 120 }}
          >
            Negotiate
          </Button>
          
          <Button
            variant="contained"
            onClick={() => onClickOption(true)}
            color="success"
            disabled={isUpdatingStatus || creator?.status === "Counter"}
            startIcon={<CheckCircle />}
            fullWidth={isMobile}
            sx={{ minWidth: 120 }}
          >
            Accept
          </Button>
        </DialogActions>
      </StyledDialog>

      {/* Negotiate Dialog */}
      {campaign && (
        <AgencyNegotiateDialog
          open={openNegotiateDialog}
          handleClose={() => setOpenNegotiateDialog(false)}
          campaign={campaign}
          creatorId={username}
          currentRate={currentRate}
          refreshCampaign={() => queryClient.invalidateQueries(["campaigns"])}
          onSuccess={() => handleCloseDialog()}
        />
      )}

      <AlertDialog alertState={dialogState} />
    </>
  );
};

export default AgencyCampaignPitchDialog;